import { alpha, createTheme } from '@mui/material/styles'

const backgroundBase = '#030712'
const backgroundElevated = '#0B1020'
const backgroundRaised = '#11162A'
const borderColor = 'rgba(255,255,255,0.08)'
const textPrimary = '#F5F7FF'
const textSecondary = '#9BA7C7'

export const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#B889FF',
      light: '#D5BAFF',
      dark: '#8B5BFF',
    },
    secondary: {
      main: '#4CE7FF',
      light: '#A1F2FF',
      dark: '#00C0E6',
    },
    background: {
      default: backgroundBase,
      paper: backgroundElevated,
    },
    text: {
      primary: textPrimary,
      secondary: textSecondary,
    },
    success: {
      main: '#4CE78A',
    },
    error: {
      main: '#FF6B6B',
    },
    warning: {
      main: '#FFB347',
    },
    info: {
      main: '#3B82F6',
    },
    divider: 'rgba(255,255,255,0.08)',
  },
  shape: {
    borderRadius: 10,
  },
  typography: {
    fontFamily:
      'var(--akem-font-family, "Inter", "Roboto", "Helvetica", "Arial", sans-serif)',
    h1: {
      fontSize: '3rem',
      fontWeight: 600,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontSize: '2.25rem',
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    h5: {
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    subtitle1: {
      color: textSecondary,
    },
    body1: {
      fontVariantNumeric: 'tabular-nums',
    },
    body2: {
      fontVariantNumeric: 'tabular-nums',
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ':root': {
          '--akem-card-glow': '0px 25px 70px rgba(4, 5, 15, 0.8)',
          '--akem-border-color': borderColor,
        },
        body: {
          backgroundColor: backgroundBase,
          backgroundImage: `
            radial-gradient(900px 600px at 90% -50%, rgba(184,137,255,0.16), transparent 60%),
            radial-gradient(1100px 600px at -10% 40%, rgba(76,231,255,0.18), transparent 60%),
            linear-gradient(180deg, #02040b 0%, #040815 70%, #02040b 100%)
          `,
          color: textPrimary,
        },
        '*': {
          scrollbarWidth: 'thin',
        },
        '*::-webkit-scrollbar': {
          width: 8,
          height: 8,
        },
        '*::-webkit-scrollbar-track': {
          backgroundColor: 'rgba(255,255,255,0.04)',
          borderRadius: 999,
        },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(255,255,255,0.18)',
          borderRadius: 999,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: alpha(backgroundElevated, 0.9),
          borderBottom: `1px solid ${borderColor}`,
          boxShadow: '0 20px 40px rgba(2,5,15,0.65)',
          backdropFilter: 'blur(16px)',
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: 72,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: backgroundElevated,
          backgroundImage:
            'linear-gradient(140deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 60%)',
          border: `1px solid ${borderColor}`,
          boxShadow: 'var(--akem-card-glow)',
          borderRadius: 12,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: `1px solid ${borderColor}`,
          backgroundImage:
            'linear-gradient(145deg, rgba(255,255,255,0.05) 0%, rgba(17,22,42,0.95) 55%, rgba(8,10,20,0.9) 100%)',
          backdropFilter: 'blur(22px)',
          boxShadow: 'var(--akem-card-glow)',
          borderRadius: 12,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
          letterSpacing: 0.2,
        },
        containedPrimary: {
          backgroundImage: 'linear-gradient(120deg, #B889FF 0%, #7B5CFF 100%)',
          boxShadow: '0 18px 45px rgba(123,92,255,0.35)',
          '&:hover': {
            backgroundImage: 'linear-gradient(120deg, #C79EFF 0%, #8B68FF 100%)',
            boxShadow: '0 22px 55px rgba(123,92,255,0.45)',
          },
        },
        outlined: {
          borderColor: alpha('#ffffff', 0.2),
          '&:hover': {
            borderColor: alpha('#ffffff', 0.35),
            backgroundColor: alpha('#B889FF', 0.08),
          },
        },
        text: {
          color: textPrimary,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          border: `1px solid ${alpha('#ffffff', 0.12)}`,
          backgroundColor: alpha('#ffffff', 0.04),
          letterSpacing: 0.4,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: alpha('#FFFFFF', 0.02),
          border: `1px solid ${alpha('#FFFFFF', 0.08)}`,
          '&:hover': {
            backgroundColor: alpha('#FFFFFF', 0.08),
          },
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          borderColor: alpha('#ffffff', 0.18),
          color: textSecondary,
          '&.Mui-selected': {
            backgroundColor: alpha('#B889FF', 0.18),
            color: textPrimary,
          },
        },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          padding: 4,
          backgroundColor: alpha('#ffffff', 0.05),
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          border: `1px solid ${borderColor}`,
          backgroundColor: backgroundRaised,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: alpha('#ffffff', 0.02),
          borderRadius: 8,
          '& fieldset': {
            borderColor: alpha('#ffffff', 0.12),
          },
          '&:hover fieldset': {
            borderColor: alpha('#ffffff', 0.35),
          },
          '&.Mui-focused fieldset': {
            borderColor: '#B889FF',
            boxShadow: `0 0 0 1px ${alpha('#B889FF', 0.25)}`,
          },
        },
        input: {
          padding: '12px 16px',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          fontWeight: 500,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: borderColor,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: backgroundRaised,
          border: `1px solid ${alpha('#ffffff', 0.15)}`,
          borderRadius: 10,
        },
      },
    },
  },
})
